import { IEquipment } from '@/pages/Equipment/equipment.types'
import equipmentUtils from '@/pages/Equipment/equipment.utils'
import Toggle from '@/shared/Inputs/Toggle'
import Skeleton from '@/shared/Loaders/Skeleton'
import { Fragment, MutableRefObject, useMemo, useRef, useState } from 'react'
import SeparateLine from '@/shared/Forms/SeparateLine'
import Button from '@/shared/Buttons/Button'
import SearchInput from '@/shared/Inputs/SearchInput'
import { Virtuoso } from 'react-virtuoso'
import equipmentAtom from '@/pages/Equipment/atoms/equipment.atom'
import { useRecoilValue } from 'recoil'
import { IEquipmentSubscriptionsChanges } from '../../hooks/useListEquipmentSubscriptionsCtrl'
import QuestionIconWithTooltip from '@/shared/Common/QuestionIconWithTooltip'

interface IProps {
  currentEquipmentSubscriptions: Set<IEquipment['id']>
  equipmentSubscriptionsChanges: MutableRefObject<IEquipmentSubscriptionsChanges>
  loading: boolean

  onChange: (v: boolean, name: string) => void
  handleUnsubscribeAll: () => void
  handleSubscribeAll: () => void
}

const ListOfEquipmentSubscriptionToggles = (props: IProps) => {
  const equipmentItems = useRecoilValue(equipmentAtom).items
  const [equipmentSearch, setEquipmentSearch] = useState('')
  const virtuosoRef = useRef(null)

  const subscriptionStatus = () => {
    return {
      isUserUnsubFromAll:
        props.currentEquipmentSubscriptions.size === props.equipmentSubscriptionsChanges.current.unsub.size &&
        props.equipmentSubscriptionsChanges.current.sub.size === 0,
      isUserSubToAll:
        equipmentItems.length ===
          props.currentEquipmentSubscriptions.size + props.equipmentSubscriptionsChanges.current.sub.size &&
        props.equipmentSubscriptionsChanges.current.unsub.size === 0,
    }
  }

  const filteredEquipmentSubscriptions = useMemo(() => {
    return equipmentItems.filter((item) =>
      equipmentUtils.getEquipmentAddress(item.location).toLowerCase().includes(equipmentSearch.toLowerCase())
    )
  }, [equipmentItems, equipmentSearch])

  const renderToggle = (index: number) => {
    const isLast = filteredEquipmentSubscriptions.length === index + 1
    const equipment = filteredEquipmentSubscriptions[index]
    if (!equipment.location?.locationDescription) return null
    const isSelected =
      props.equipmentSubscriptionsChanges.current.sub.has(equipment.id) ||
      (props.currentEquipmentSubscriptions.has(equipment.id) &&
        !props.equipmentSubscriptionsChanges.current.unsub.has(equipment.id))

    return (
      <div key={equipment.id} className="flex-lines" style={{ width: 'calc(100% - 24px)' }}>
        <div className="flex-space-between">
          <div className="flex-lines gap4">
            <p className="text-nm">{equipmentUtils.getEquipmentAddress(equipment.location)}</p>
            <span style={{ textTransform: 'capitalize' }} className="text-sm text-secondary">
              {equipment.location.locationDescription}
            </span>
          </div>
          <Toggle
            name={equipment.id.toString()}
            checked={isSelected}
            onChange={props.onChange}
            size="large"
            style={{ flexShrink: 0 }}
          />
        </div>
        {!isLast && <SeparateLine style={{ margin: '12px 0' }} />}
      </div>
    )
  }

  const renderToggles = () => {
    if (props.loading)
      return (
        <div className="flex-lines gap12" style={{ paddingRight: '24px', flex: '1 1 0' }}>
          {Array.from({ length: 4 }, (_, i) => (
            <Fragment key={i}>
              <Skeleton key={i} height="42px" width="100%" />
              {i !== 3 && <SeparateLine />}
            </Fragment>
          ))}
        </div>
      )
    return (
      <Virtuoso
        ref={virtuosoRef}
        totalCount={filteredEquipmentSubscriptions.length}
        itemContent={(index) => renderToggle(index)}
      />
    )
  }

  return (
    <>
      <div className="flex-lines gap16" style={{ paddingRight: '24px' }}>
        <div className="inline-flex-gap gap8 center">
          <h5 className="text-heading-5">Лифты</h5>
          <QuestionIconWithTooltip
            text="Вы будете получать уведомления об ошибках и ТО только по выбранным лифтам."
            position="right"
          />
        </div>
        <SearchInput
          inputStyle={{ width: '100%' }}
          value={equipmentSearch}
          name="equipmentSubSearch"
          onChange={setEquipmentSearch}
          placeHolder="Поиск по лифтам и адресам"
        />
        <div className="inline-flex-gap gap8">
          <Button
            size="default"
            type="default"
            text="Включить все"
            onClick={props.handleSubscribeAll}
            disabled={subscriptionStatus().isUserSubToAll || props.loading}
          />
          <Button
            size="default"
            type="default"
            text="Выключить все"
            onClick={props.handleUnsubscribeAll}
            disabled={subscriptionStatus().isUserUnsubFromAll || props.loading}
          />
        </div>
      </div>
      {renderToggles()}
    </>
  )
}

export default ListOfEquipmentSubscriptionToggles
